import React, { useEffect, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import {
  AdminResourceDeleteModal,
  AdminSearchInput,
  FiltersBackground,
  PageHeader,
  PageMain,
} from "components/molecules";

import { DISCOUNT_TYPES, routes } from "consts";
import {
  useFilters,
  useSorting,
  usePagination,
  TanTable,
  TanTableColumn,
} from "components/molecules";
import { formatDate, useToaster } from "utils";
import { DiscountCodeApi, discountsApi } from "api/payment-discounts";
import { useDiscounts } from "api/payment-discounts";
import { useDeleteResourceActions } from "utils/store";

export const DiscountsPage = () => {
  const { setDeleteResource } = useDeleteResourceActions();
  const { showError, showSuccess } = useToaster();
  const { limit, onPaginationChange, skip, pagination } = usePagination();
  const { searchPhrase, filter, setSearchPhrase } = useFilters();
  const { sorting, onSortingChange, fieldWithOrder } = useSorting();

  const { isFetching, discounts, invalidate, total, isLoading } = useDiscounts(
    skip,
    limit,
    searchPhrase,
    filter,
    fieldWithOrder,
  );

  useEffect(() => {
    invalidate();
  }, [sorting, pagination, searchPhrase, filter]);

  const navigate = useNavigate();

  const onToggleDisabled = async (row: DiscountCodeApi, nextIsDisabled: boolean) => {
    try {
      await discountsApi.setDisabled(row.id, nextIsDisabled);
      showSuccess(nextIsDisabled ? "Rabat wyłączony" : "Rabat włączony");
      invalidate();
    } catch (e) {
      console.error(e);
      showError("Nie udało się zmienić statusu rabatu");
    }
  };

  const columns = React.useMemo<TanTableColumn<DiscountCodeApi>[]>(
    () => buildCols(onToggleDisabled),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  return (
    <PageMain>
      <PageHeader
        button={{
          title: "Dodaj +",
          onClick: () => navigate(routes.DISCOUNTS_ADD),
        }}
        secondaryButton={{
          title: "Naruszone rabaty",
          onClick: () => navigate(routes.DISCOUNTS_VIOLATED_BUNDLES),
        }}
      >
        Kody rabatowe
      </PageHeader>

      <FiltersBackground className="mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <AdminSearchInput
            onSearch={(searchPhrase: string) => setSearchPhrase(searchPhrase)}
            className="mt-2 self-end"
          />
        </div>
      </FiltersBackground>
      <TanTable
        data={discounts}
        columns={columns}
        total={total}
        loading={isLoading}
        fetching={isFetching}
        onPaginationChange={onPaginationChange}
        onSortingChange={onSortingChange}
        onDelete={(x) => setDeleteResource(x.id)}
        pagination={pagination}
        sorting={sorting}
        onRowClick={(x) => navigate(routes.DISCOUNTS_EDIT(x.id))}
      />
      <AdminResourceDeleteModal
        title="Usuń kod rabatowy"
        description="Jesteś pewien że chcesz usunąć kod rabatowy?"
        onSuccessDelete={() => invalidate()}
        deleteAction={discountsApi.delete}
      />
    </PageMain>
  );
};

const buildCols = (
  onToggleDisabled: (row: DiscountCodeApi, nextIsDisabled: boolean) => void,
): TanTableColumn<DiscountCodeApi>[] => [
  {
    id: "id",
    accessorKey: "id",
    header: "ID",
    enableColumnFilter: false,
    size: 10,
    cell: (info) => info.getValue(),
  },
  {
    id: "isDisabled",
    accessorKey: "isDisabled",
    header: "Aktywny",
    enableSorting: false,
    size: 80,
    cell: (info) => {
      const row = info.row.original;
      const isDisabled = !!row.isDisabled;
      return (
        <label
          className="inline-flex items-center cursor-pointer"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            type="checkbox"
            className="form-checkbox h-5 w-5 text-primary-500"
            checked={!isDisabled}
            onChange={(e) => onToggleDisabled(row, !e.target.checked)}
            title={isDisabled ? "Włącz rabat" : "Wyłącz rabat"}
          />
        </label>
      );
    },
  },
  {
    id: "code",
    accessorKey: "code",
    header: "Kod",
  },
  {
    id: "mode",
    accessorKey: "mode",
    header: "Tryb",
    cell: (info) => {
      const mode = info.getValue<string>();
      switch (mode) {
        case "bundle":
          return <span className="font-medium text-blue-600">Pakiet</span>;
        case "standard":
        default:
          return <span className="text-gray-600 dark:text-slate-400">Standard</span>;
      }
    },
    enableSorting: false,
  },
  {
    id: "courses",
    header: "Kursy",
    cell: (info) => {
      const row = info.row.original;
      const mode = row.mode || "standard";

      if (mode === "bundle") {
        const required = row.requiredCourses?.length || 0;
        const benefit = row.benefitCourses?.length || 0;
        return (
          <span className="text-sm">
            {required} wymagane → {benefit} ze zniżką
          </span>
        );
      } else if (row.course) {
        return <span className="text-sm">{row.course.name}</span>;
      } else if (row.courseGroup) {
        return <span className="text-sm">Grupa: {row.courseGroup.name}</span>;
      } else if (row.categories && row.categories.length > 0) {
        return <span className="text-sm">Kategorie: {row.categories.map((c) => c.name).join(", ")}</span>;
      } else if (row.user) {
        return <span className="text-sm">User: {row.user.email}</span>;
      } else {
        return <span className="text-sm text-gray-400">Ogólny</span>;
      }
    },
    enableSorting: false,
  },
  {
    id: "from",
    accessorKey: "from",
    cell: (info) => formatDate(info.getValue<Date>()),
    header: "Od",
  },
  {
    id: "to",
    accessorKey: "to",
    cell: (info) => formatDate(info.getValue<Date>()),
    header: "Do",
  },
  {
    id: "description",
    accessorKey: "description",
    header: "Opis",
  },
  {
    id: "numberOfUses",
    accessorKey: "numberOfUses",
    header: "Liczba użyc",
  },
  {
    id: "type",
    accessorKey: "type",
    header: "Rodzaj zniżki",
    cell: (info) => {
      switch (info.getValue()) {
        case DISCOUNT_TYPES.PERCENTAGE:
          return <span>{info.row.original.value} %</span>;
        case DISCOUNT_TYPES.AMOUNT:
          return <span>{info.row.original.value} PLN</span>;
        default:
          return "Nieznany";
      }
    },
    enableSorting: false,
  },
];
